import React, { useMemo } from "react";

import Checkbox from "@cayuse/shared-react.components.form.inputs.checkbox";
import { isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";

import Tooltip from "components/Tooltip";
import styles from "containers/Form/SimplifiedBuilder/Components/ClassificationCode/styles.css";
import { useDictionary } from "hooks";
import { DEFAULT_MAXIMUM_BY_CLASSIFICATION_CODE_TYPE } from "globals/constants";

import ClassificationCodeNumberOfCodesInput from "./ClassificationCodeNumberOfCodesInput";


const INPUT_TOOLTIP_TEXT = `Please note that if the \"Required\" check box is selected,
only the fields that are visible on the form are required. The person filling out the form will be able to
add or remove additional required fields. Minimum number of codes will always be required.`;

const CODE_TYPES_WITH_HAS_FOUR_DIGITS = [ "FOR", "SEO" ];
const CODE_TYPES_WITH_EXCLUDE_SUBCATEGORIES = [ "HERDC" ];

const DEFAULT_DICTIONARY = {
	limitToFourDigits: "Limit to four digit codes (Division and Group only)",
	excludeSubcategories: "Exclude subcategories from options",
	includePercentage: "Include Percentage (Percentages are required to equal 100%)",
	minNumberOfCodes: "Minimum Number of Codes",
	minNumberOfCodesExplanation: " - this many codes are the minimum that will appear on the form",
	maxNumberOfCodes: "Maximum Number of Codes",
	maxNumberOfCodesExplanation: " - up to this many codes may be added to the form",
};

export const ClassificationCodeCustomSettings = ({ parentQuestionConfig, onChangeConfig }) => {
	const dictionary = useDictionary("simplifiedBuilder.components.classificationCodeSettings.errors");

	const extraSettings = parentQuestionConfig.extraSettings;
	const codeTypeLowerCase = extraSettings.codeType.toLowerCase();

	const hasLimitToFourDigits = useMemo(
		() => CODE_TYPES_WITH_HAS_FOUR_DIGITS.includes(parentQuestionConfig?.extraSettings?.codeType),
		[ parentQuestionConfig ],
	);
	const hasExcludeCategories = useMemo(
		() => CODE_TYPES_WITH_EXCLUDE_SUBCATEGORIES.includes(parentQuestionConfig?.extraSettings?.codeType),
		[ parentQuestionConfig ],
	);
	const onChangeExtraSettings = newExtraSettings => onChangeConfig({
		extraSettings: { ...extraSettings, ...newExtraSettings },
	});

	const { minimum, maximum, defaultMinimum, defaultMaximum } = useMemo(
		() => {
			let minimum = "";
			let maximum = "";
			if (isNonEmptyString(extraSettings.codeType)) {
				minimum = Number(extraSettings?.[`${codeTypeLowerCase}Minimum`]) || "";
				maximum = Number(extraSettings?.[`${codeTypeLowerCase}Maximum`]) || "";
			}

			return {
				minimum,
				maximum,
				defaultMinimum: 1,
				defaultMaximum: DEFAULT_MAXIMUM_BY_CLASSIFICATION_CODE_TYPE[extraSettings.codeType],
			};
		},
		[ extraSettings, codeTypeLowerCase ],
	);

	const updateNumberOfCodes = (name, value) => {
		onChangeExtraSettings({ [name]: value})
	}

	const hasCodeType = isNonEmptyString(extraSettings.codeType);

	const onCheckboxChange = (event, data) => {
		onChangeExtraSettings({ [data.name]: data.checked })
	};

	return (
		<>
			{hasLimitToFourDigits && (
				<div className={`ui input ${styles.extraSettingsSetting}`}>
					<Checkbox
						label={dictionary.limitToFourDigits || DEFAULT_DICTIONARY.limitToFourDigits}
						checked={extraSettings.limitToFourDigits}
						name="limitToFourDigits"
						onChange={onCheckboxChange}
					/>
				</div>
			)}
			{hasExcludeCategories && (
				<div className={`ui input ${styles.extraSettingsSetting}`}>
					<Checkbox
						label={dictionary.excludeSubcategories || DEFAULT_DICTIONARY.excludeSubcategories}
						checked={extraSettings.excludeSubcategories}
						name="excludeSubcategories"
						onChange={onCheckboxChange}
					/>
				</div>
			)}
			<div className={`ui input ${styles.extraSettingsSetting}`}>
				<Checkbox
					label={dictionary.include || DEFAULT_DICTIONARY.includePercentage}
					checked={extraSettings.includePercentage}
					name="includePercentage"
					onChange={onCheckboxChange}
				/>
			</div>
			<div className={`ui input ${styles.extraSettingsSetting}`}>
				<p>
					<strong>{dictionary.minNumberOfCodes || DEFAULT_DICTIONARY.minNumberOfCodes}</strong>
					{dictionary.minNumberOfCodesExplanation || DEFAULT_DICTIONARY.minNumberOfCodesExplanation}
				</p>
				<ClassificationCodeNumberOfCodesInput
					value={minimum}
					defaultValue={defaultMinimum}
					type="minimum"
					codeType={extraSettings.codeType}
					name={`${codeTypeLowerCase}Minimum`}
					disabled={!hasCodeType}
					onChange={updateNumberOfCodes}
					minimum={minimum}
					maximum={maximum}
				/>
			</div>
			<div className={`ui input ${styles.extraSettingsSetting}`}>
				<p>
					<Tooltip id={`${codeTypeLowerCase}Maximum`} tooltipContent={INPUT_TOOLTIP_TEXT}>
						<span>
							<strong>{dictionary.maxNumberOfCodes || DEFAULT_DICTIONARY.maxNumberOfCodes}</strong>
							{dictionary.maxNumberOfCodesExplanation || DEFAULT_DICTIONARY.maxNumberOfCodesExplanation}
						</span>
					</Tooltip>
				</p>
				<ClassificationCodeNumberOfCodesInput
					value={maximum}
					defaultValue={defaultMaximum}
					type="maximum"
					codeType={extraSettings.codeType}
					name={`${codeTypeLowerCase}Maximum`}
					disabled={!hasCodeType}
					onChange={updateNumberOfCodes}
					minimum={minimum}
					maximum={maximum}
				/>
			</div>
		</>
	);
};
